import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import { useRef, useEffect } from 'react';
import { f as dateUtils, n as getLocaleDateString, c as EGDSCalendarNavigation, o as getMonthYear } from '../legend-swatches/index-chunk.esm.js';
import '../styles/uitk-egds-calendar.css';
import { d as EGDSLayoutPosition } from '../layout-position/index-chunk.esm.js';
import { g as EGDSFakeInput } from '../swapper-input/index-chunk.esm.js';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from '@shared-ui/viewport-context';
import { c as classNames } from '../theme-provider/index-chunk.esm.js';
import { h as EGDSSheetTransition, e as EGDSSheet, f as EGDSSheetContent, i as EGDSSheetFooter } from '../sheet-footer/index-chunk.esm.js';
import { b as EGDSSpacing } from '../spacing-hr/index-chunk.esm.js';
import { E as EGDSButtonSize } from '../button/index-chunk.esm.js';
import { E as EGDSPrimaryButton } from '../primary-button/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import { b as EGDSToolbar, e as EGDSToolbarButton } from '../toolbar-button/index-chunk.esm.js';
import styles from '../styles/uitk-egds-date-selector.css';
import { useDialog } from '@shared-ui/dialog-context';
const {
  getEffectiveConsolidatedDateString,
  getEffectiveDateText
} = dateUtils;
const EGDSDateSelectorInputs = props => {
  const l10n = useLocalization();
  const defaultInputLabel1 = l10n.formatText('dateSelector.startDate.label');
  const defaultInputLabel2 = l10n.formatText('dateSelector.endDate.label');
  const {
    dialogId,
    input1Stid,
    input2Stid,
    dateFormatOptions,
    endDate,
    handleClickInput1,
    handleClickInput2,
    input1Ref,
    input2Ref,
    inputIcon1 = 'today',
    inputIcon2 = 'today',
    inputLabel1 = defaultInputLabel1,
    inputLabel2 = defaultInputLabel2,
    inputName1,
    inputName2,
    invalidInput1,
    invalidInput2,
    isSingleDateSelection,
    locale,
    startDate,
    trailingText1,
    trailingText2,
    useSingleTrigger,
    inputValue1: customInputValue1,
    inputValue2: customInputValue2,
    isOpen = false
  } = props;
  const firstInputDataStid = `uitk-date-selector-input1-${input1Stid ?? dialogId}`;
  const secondInputDataStid = `uitk-date-selector-input2-${input2Stid ?? dialogId}`;
  const [openedInput, setOpenedInput] = React.useState(null);
  const isMultiInput = React.useMemo(() => !isSingleDateSelection && !useSingleTrigger, [isSingleDateSelection, useSingleTrigger]);
  const [inputValue1, inputValue2] = React.useMemo(() => {
    const input1 = useSingleTrigger ? getEffectiveConsolidatedDateString(startDate ?? '', endDate ?? '', locale, dateFormatOptions) : getEffectiveDateText(startDate, locale, dateFormatOptions);
    const input2 = getEffectiveDateText(endDate, locale, dateFormatOptions);
    return [input1, input2];
  }, [dateFormatOptions, endDate, locale, startDate, useSingleTrigger]);
  const [inputId1, inputId2] = React.useMemo(() => [Math.random().toString(36).substring(7), Math.random().toString(36).substring(7)], []);
  const handleClickInput1Modified = e => {
    handleClickInput1(e);
    setOpenedInput('input1');
  };
  const handleClickInput2Modified = e => {
    handleClickInput2(e);
    setOpenedInput('input2');
  };
  return jsxs(EGDSLayoutFlex, {
    direction: "row",
    space: "three",
    children: [jsx(EGDSLayoutFlexItem, {
      grow: 1,
      maxWidth: isMultiInput ? 'half_width' : 'full_width',
      children: jsx("div", {
        children: jsxs(Viewport, {
          children: [jsx(ViewSmall, {
            children: jsx(EGDSFakeInput, {
              "data-testid": firstInputDataStid,
              "data-stid": firstInputDataStid,
              domRef: input1Ref,
              icon: inputIcon1,
              id: inputId1,
              label: inputLabel1,
              name: inputName1,
              onClick: handleClickInput1Modified,
              value: customInputValue1 ?? inputValue1,
              trailingText: trailingText1,
              invalid: invalidInput1,
              "aria-invalid": !!invalidInput1,
              "aria-describedby": `${inputId1}-error`
            })
          }), jsx(ViewMedium, {
            children: jsx(EGDSFakeInput, {
              "data-testid": firstInputDataStid,
              "data-stid": firstInputDataStid,
              domRef: input1Ref,
              icon: inputIcon1,
              id: inputId1,
              label: inputLabel1,
              name: inputName1,
              onClick: handleClickInput1Modified,
              value: customInputValue1 ?? inputValue1,
              trailingText: trailingText1,
              invalid: invalidInput1,
              "aria-invalid": !!invalidInput1,
              "aria-describedby": `${inputId1}-error`,
              "aria-expanded": isOpen && openedInput === 'input1'
            })
          })]
        })
      })
    }), isMultiInput && jsx(EGDSLayoutFlexItem, {
      grow: 1,
      maxWidth: "half_width",
      children: jsx("div", {
        children: jsxs(Viewport, {
          children: [jsx(ViewSmall, {
            children: jsx(EGDSFakeInput, {
              "data-testid": secondInputDataStid,
              "data-stid": secondInputDataStid,
              domRef: input2Ref,
              icon: inputIcon2,
              id: inputId2,
              label: inputLabel2,
              name: inputName2,
              onClick: handleClickInput2Modified,
              value: customInputValue2 ?? inputValue2,
              trailingText: trailingText2,
              invalid: invalidInput2,
              "aria-invalid": !!invalidInput2,
              "aria-describedby": `${inputId2}-error`
            })
          }), jsx(ViewMedium, {
            children: jsx(EGDSFakeInput, {
              "data-testid": secondInputDataStid,
              "data-stid": secondInputDataStid,
              domRef: input2Ref,
              icon: inputIcon2,
              id: inputId2,
              label: inputLabel2,
              name: inputName2,
              onClick: handleClickInput2Modified,
              value: customInputValue2 ?? inputValue2,
              trailingText: trailingText2,
              invalid: invalidInput2,
              "aria-invalid": !!invalidInput2,
              "aria-describedby": `${inputId2}-error`,
              "aria-expanded": isOpen && openedInput === 'input2'
            })
          })]
        })
      })
    })]
  });
};
EGDSDateSelectorInputs.displayName = 'EGDSDateSelectorInputs';
const EGDSAlert = ({
  alertTemplateComponent: AlertTemplateComponent,
  alertTemplateData
}) => jsx("div", {
  children: jsx(AlertTemplateComponent, {
    ...alertTemplateData
  })
});
const getClassNameList$5 = classNames.bind(styles);
const dateSelectorPopoverClassName = 'uitk-date-selector-popover';
const PopoverCalendar = props => {
  const {
    isOpen,
    triggerRef,
    children,
    ctaItems,
    dateSelectorPlayback,
    hideFooter,
    isSingleMonth,
    showPlayback,
    showOffset,
    calendarWrapper,
    alertTemplateComponent,
    alertTemplateData,
    popoverWidth,
    initialOpenState
  } = props;
  const {
    submitCta,
    clearDatesCta
  } = ctaItems;
  const CalendarWrapper = React.useMemo(() => calendarWrapper ? calendarWrapper.component : React.Fragment, [calendarWrapper]);
  const dateSelectorPopoverClasses = getClassNameList$5(dateSelectorPopoverClassName, {
    'uitk-date-selector-popover-single': isSingleMonth
  });
  // Focus should happen only when the component is open/close via user interactions (trigger and close button)
  const [shouldFocus, setShouldFocus] = React.useState(!initialOpenState);
  const toggleCallback = () => {
    if (!shouldFocus) {
      setShouldFocus(true);
    }
  };
  return jsx(EGDSSheetTransition, {
    isVisible: isOpen,
    openCallback: toggleCallback,
    children: jsxs(EGDSSheet, {
      isVisible: isOpen,
      type: "popover",
      triggerRef: triggerRef,
      onDismiss: ctaItems.closeCta.handler,
      className: getClassNameList$5(dateSelectorPopoverClasses),
      returnFocusOnClose: shouldFocus,
      showOverTrigger: !showOffset,
      applyBlockStartMargin: !!showOffset,
      disableArrowKeyHandler: true,
      width: popoverWidth,
      autoFocus: shouldFocus,
      children: [jsxs(CalendarWrapper, {
        ...calendarWrapper?.props,
        children: [showPlayback && jsx(EGDSToolbar, {
          childClassName: getClassNameList$5('uitk-date-selector-playback-toolbar'),
          children: dateSelectorPlayback
        }), jsx(EGDSSheetContent, {
          padded: false,
          className: getClassNameList$5(`${dateSelectorPopoverClassName}-content`),
          children: children
        })]
      }), alertTemplateComponent && jsx(EGDSAlert, {
        alertTemplateComponent: alertTemplateComponent,
        alertTemplateData: alertTemplateData
      }), !hideFooter && jsx(EGDSSpacing, {
        padding: {
          inline: 'two',
          block: 'three'
        },
        children: jsx("footer", {
          className: getClassNameList$5('uitk-sheet-footer'),
          children: jsxs(EGDSLayoutFlex, {
            space: "three",
            justifyContent: "end",
            children: [clearDatesCta.handler && jsx(EGDSLayoutFlexItem, {
              children: jsx(EGDSTertiaryButton, {
                onClick: clearDatesCta.handler,
                "aria-disabled": clearDatesCta.disabled,
                tabIndex: clearDatesCta.disabled ? -1 : 0,
                children: clearDatesCta.label
              })
            }), submitCta.handler && jsx(EGDSLayoutFlexItem, {
              children: jsx(EGDSPrimaryButton, {
                "data-stid": "apply-date-selector",
                disabled: submitCta.disabled,
                onClick: submitCta.handler,
                children: submitCta.label
              })
            })]
          })
        })
      })]
    })
  });
};
const getClassNameList$4 = classNames.bind(styles);
const ComposableCalendar = props => {
  const {
    children,
    ctaItems,
    dateSelectorPlayback,
    showPlayback,
    calendarWrapper,
    alertTemplateComponent,
    alertTemplateData,
    hideFooter,
    navType
  } = props;
  const {
    submitCta,
    clearDatesCta
  } = ctaItems;
  const CalendarWrapper = React.useMemo(() => calendarWrapper ? calendarWrapper.component : React.Fragment, [calendarWrapper]);
  const navTypeIsScrolling = navType === 'scrolling';
  const baseDateSelectorPlaybackWrapperClass = 'uitk-date-selector-playback-wrapper';
  const dateSelectorPlaybackWrapperCssClasses = {
    'uitk-date-selector-playback-wrapper-sticky': navTypeIsScrolling
  };
  const baseDateSelectorFooterClass = 'uitk-date-selector-footer';
  const dateSelectorFooterCssClasses = {
    'uitk-date-selector-footer-sticky': navTypeIsScrolling
  };
  return jsxs(Fragment, {
    children: [jsxs(CalendarWrapper, {
      ...calendarWrapper?.props,
      children: [showPlayback && jsx("div", {
        "data-testid": "date-selector-playback-wrapper",
        className: getClassNameList$4(baseDateSelectorPlaybackWrapperClass, dateSelectorPlaybackWrapperCssClasses),
        children: jsx(EGDSToolbar, {
          children: dateSelectorPlayback
        })
      }), jsx("div", {
        children: children
      })]
    }), alertTemplateComponent && !navTypeIsScrolling && jsx(EGDSAlert, {
      alertTemplateComponent: alertTemplateComponent,
      alertTemplateData: alertTemplateData
    }), !hideFooter && jsx(EGDSSpacing, {
      padding: {
        inline: 'two',
        block: 'three'
      },
      children: jsxs("footer", {
        className: getClassNameList$4(baseDateSelectorFooterClass, dateSelectorFooterCssClasses),
        "data-testid": "date-selector-footer",
        children: [alertTemplateComponent && navTypeIsScrolling && jsx(EGDSAlert, {
          alertTemplateComponent: alertTemplateComponent,
          alertTemplateData: alertTemplateData
        }), jsxs(EGDSLayoutFlex, {
          space: "three",
          justifyContent: navTypeIsScrolling ? 'center' : 'end',
          children: [clearDatesCta.handler && !navTypeIsScrolling && jsx(EGDSLayoutFlexItem, {
            children: jsx(EGDSTertiaryButton, {
              onClick: clearDatesCta.handler,
              "aria-disabled": clearDatesCta.disabled,
              tabIndex: clearDatesCta.disabled ? -1 : 0,
              children: clearDatesCta.label
            })
          }), submitCta.handler && jsx(EGDSLayoutFlexItem, {
            children: jsx(EGDSPrimaryButton, {
              "data-stid": "apply-date-selector",
              disabled: submitCta.disabled,
              onClick: submitCta.handler,
              isFullWidth: navTypeIsScrolling,
              size: navTypeIsScrolling ? EGDSButtonSize.LARGE : EGDSButtonSize.MEDIUM,
              children: submitCta.label
            })
          })]
        })]
      })
    })]
  });
};
const getClassNameList$3 = classNames.bind(styles);
const buttonUID = Math.random().toString(36).substring(7);
const DialogCalendar = props => {
  const {
    alertTemplateComponent,
    alertTemplateData,
    children,
    calendarWrapper,
    skipToButtonRef,
    ctaItems,
    dateSelectorPlayback,
    dialogId,
    hideDialogSubmit,
    hideFooter,
    isOpen,
    sheetType,
    showFullSizePlaybackDateDisplay = true,
    showPlayback,
    triggerRef,
    isNestDialog
  } = props;
  const {
    closeCta,
    submitCta,
    clearDatesCta
  } = ctaItems;
  const l10n = useLocalization();
  const dialogQueryKey = isNestDialog ? 'pwaDialogNested' : undefined;
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog(`uitk-date-selector-dialog-${dialogId}`, dialogQueryKey);
  const sheetBaseClassName = sheetType === 'centered' ? 'uitk-date-selector-centered-sheet' : 'uitk-date-selector-fullsheet';
  const sheetClassNames = getClassNameList$3(sheetBaseClassName, {
    'uitk-date-selector-dialog-sheet-full-playback-display': showFullSizePlaybackDateDisplay
  });
  const wrapperContainerClass = 'uitk-date-selector-dialog-external-wrapper-container';
  const externalWrapperSheetClass = sheetType === 'centered' ? `${wrapperContainerClass}-centered-sheet` : `${wrapperContainerClass}-fullsheet`;
  const dateSelectorCalendarClassName = `uitk-date-selector-dialog`;
  const CalendarWrapper = React.useMemo(() => calendarWrapper ? calendarWrapper.component : React.Fragment, [calendarWrapper]);
  const prevIsDialogOpenRef = useRef(isDialogOpen);
  React.useEffect(() => {
    if (isOpen && !isDialogOpen) {
      // If the dialog has been closed via the escape key or by clicking the backdrop we need to update the interal isOpen state via the closeCta handler
      if (prevIsDialogOpenRef.current !== isDialogOpen) {
        closeCta.handler?.();
      } else {
        dialogActions.openDialog();
      }
    } else if (!isOpen && isDialogOpen) {
      // Prevent a history.goBack() call by passing additionalQueryParams
      dialogActions.closeDialog({
        additionalQueryParams: {}
      });
    }
    prevIsDialogOpenRef.current = isDialogOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDialogOpen]); // Do not add dialogActions or this will always update
  return jsx("div", {
    className: getClassNameList$3(dateSelectorCalendarClassName),
    children: jsx(EGDSSheetTransition, {
      isVisible: isOpen,
      children: jsx(DialogComponent, {
        children: jsxs(EGDSSheet, {
          centeredSheetSize: sheetType === 'centered' ? 'small' : undefined,
          className: sheetClassNames,
          disableArrowKeyHandler: true,
          isVisible: isDialogOpen,
          returnFocusOnClose: false,
          triggerRef: triggerRef,
          type: sheetType,
          children: [jsx(EGDSToolbar, {
            childClassName: getClassNameList$3('uitk-calendar-limit-inline-size'),
            navigationContent: {
              navIconLabel: l10n.formatText('dateSelector.cancelButton.a11yLabel'),
              navType: 'close',
              // @ts-ignore
              onClick: closeCta.handler
            },
            toolbarActionContent: clearDatesCta?.handler ? jsx(EGDSToolbarButton, {
              buttonId: buttonUID,
              onClick: clearDatesCta.handler,
              disabled: clearDatesCta.disabled,
              children: clearDatesCta.label
            }) : null,
            children: !calendarWrapper && showPlayback && dateSelectorPlayback
          }), calendarWrapper ? jsxs(CalendarWrapper, {
            ...calendarWrapper?.props,
            className: getClassNameList$3(`${wrapperContainerClass} ${externalWrapperSheetClass}`),
            children: [calendarWrapper && showPlayback && dateSelectorPlayback, jsx(EGDSSheetContent, {
              padded: false,
              children: jsx("div", {
                className: getClassNameList$3('uitk-date-selector-dialog-calendar'),
                children: children
              })
            })]
          }) : jsx(EGDSSheetContent, {
            padded: false,
            children: children
          }), !hideFooter && (alertTemplateComponent || !hideDialogSubmit) && jsx(EGDSSpacing, {
            padding: {
              inline: 'three',
              block: 'three'
            },
            children: jsxs(EGDSSheetFooter, {
              children: [alertTemplateComponent && jsx(EGDSAlert, {
                alertTemplateComponent: alertTemplateComponent,
                alertTemplateData: alertTemplateData
              }), !hideDialogSubmit && jsx(EGDSPrimaryButton, {
                "data-stid": "apply-date-selector",
                disabled: submitCta.disabled,
                domRef: skipToButtonRef,
                isFullWidth: true,
                onClick: submitCta.handler,
                size: EGDSButtonSize.LARGE,
                children: submitCta.label
              })]
            })
          })]
        })
      })
    })
  });
};
const getClassNameList$2 = classNames.bind(styles);
const dateDisplayClassName = 'uitk-date-display';
const EGDSDateDisplay = props => {
  const {
    id,
    locale = 'en-US',
    label = 'Date',
    date,
    isActive = false,
    isFullSize = false,
    dateFormatOptions = {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    },
    onDateDisplayClick
  } = props;
  const value = date ? getLocaleDateString(date, locale, dateFormatOptions) : label;
  const selectionButtonClasses = getClassNameList$2(dateDisplayClassName, {
    'selection-filled': date,
    selected: isActive,
    'full-size': isFullSize
  });
  return jsx("button", {
    type: "button",
    "aria-pressed": isActive,
    id: id,
    className: selectionButtonClasses,
    onClick: onDateDisplayClick,
    children: jsx("span", {
      className: `${dateDisplayClassName}-value`,
      children: value
    })
  });
};
const getClassNameList$1 = classNames.bind(styles);
const dateSelectorPlaybackClassName = 'uitk-date-selector-playback';
const arrowIconClassName = `${dateSelectorPlaybackClassName}-icon`;
const EGDSDateSelectorPlayback = props => {
  const {
    locale,
    startLabel,
    endLabel,
    startDate,
    endDate,
    dateFormatOptions,
    updateSelectionChangeMode = () => {},
    selectionChangeMode,
    isMultiPlaybackDateDisplay,
    isFullSizePlaybackDateDisplay
  } = props;
  const l10n = useLocalization();
  const isStartActive = selectionChangeMode === 'start';
  const isEndActive = !isStartActive;
  const defaultStartLabel = l10n.formatText('dateSelector.startDate.label');
  const defaultEndLabel = l10n.formatText('dateSelector.endDate.label');
  const dateDisplayId1 = Math.random().toString(36).substring(7);
  const dateDisplayId2 = Math.random().toString(36).substring(7);
  const handleClickStartDateDisplay = () => {
    updateSelectionChangeMode('start');
  };
  const handleClickEndDateDisplay = () => {
    // If there is an end date, allow user to select end date-display
    // Otherwise do nothing
    if (endDate) {
      updateSelectionChangeMode('end');
    }
  };
  return jsxs(EGDSLayoutFlex, {
    "data-testid": "date-selector-playback",
    direction: "row",
    alignItems: "center",
    className: getClassNameList$1(dateSelectorPlaybackClassName),
    children: [jsx(EGDSLayoutFlexItem, {
      children: jsx(EGDSDateDisplay, {
        id: dateDisplayId1,
        locale: locale,
        label: startLabel || defaultStartLabel,
        date: startDate || null,
        dateFormatOptions: dateFormatOptions,
        isActive: isStartActive,
        isFullSize: isFullSizePlaybackDateDisplay,
        onDateDisplayClick: handleClickStartDateDisplay
      })
    }), isMultiPlaybackDateDisplay && jsxs(Fragment, {
      children: [jsx(EGDSLayoutFlexItem, {
        children: jsx(EGDSSpacing, {
          margin: {
            inline: 'three'
          },
          children: jsx(EGDSIcon, {
            name: "arrow_forward",
            size: EGDSIconSize.SMALL,
            className: getClassNameList$1(arrowIconClassName)
          })
        })
      }), jsx(EGDSLayoutFlexItem, {
        children: jsx(EGDSDateDisplay, {
          id: dateDisplayId2,
          locale: locale,
          label: endLabel || defaultEndLabel,
          date: endDate || null,
          dateFormatOptions: dateFormatOptions,
          isActive: isEndActive,
          isFullSize: isFullSizePlaybackDateDisplay,
          onDateDisplayClick: handleClickEndDateDisplay
        })
      })]
    })]
  });
};
const getClassNameList = classNames.bind(styles);
const EGDSDateSelectorAdapter = props => {
  const l10n = useLocalization();
  const defaultClearButtonLabel = l10n.formatText('calendar.clearDatesButton.label');
  const defaultSubmitButtonLabel = l10n.formatText('calendar.submitButton.label');
  const {
    allowClearDates,
    clearButtonLabel = defaultClearButtonLabel,
    dialogId,
    endDate,
    calendarWrapper,
    customAriaLabels,
    onSubmit,
    hideFooter,
    hideDialogSubmit,
    isOpen,
    isFullSizePlaybackDateDisplay,
    isSingleDateSelection,
    numColumns,
    numMonthsToDisplay,
    onClose,
    onDatesCleared,
    showOffset,
    showPlayback,
    startDate,
    submitButtonLabel = defaultSubmitButtonLabel,
    triggerRef,
    locale,
    datesInHeaderLabels,
    selectionChangeMode,
    updateSelectionChangeMode,
    calendarRef,
    dateFormatOptions,
    displayType,
    popoverWidth,
    isNestDialog,
    initialOpenState
  } = props;
  const handleClose = React.useCallback(() => {
    const shouldClear = !(startDate && (isSingleDateSelection || endDate));
    onClose && onClose(shouldClear);
  }, [endDate, isSingleDateSelection, onClose, startDate]);
  const handleSubmit = React.useCallback(() => {
    if (!startDate && !endDate || startDate && isSingleDateSelection || startDate && endDate) {
      onSubmit && onSubmit(startDate ?? null, endDate ?? null);
    }
    handleClose();
  }, [endDate, handleClose, isSingleDateSelection, onSubmit, startDate]);
  const disableClearButton = !startDate && (!endDate || isSingleDateSelection);
  const ctaItems = React.useMemo(() => ({
    closeCta: {
      handler: handleClose
    },
    submitCta: {
      label: submitButtonLabel,
      handler: handleSubmit
    },
    clearDatesCta: {
      label: clearButtonLabel,
      disabled: disableClearButton,
      ...(allowClearDates && {
        handler: onDatesCleared
      })
    }
  }), [handleClose, submitButtonLabel, clearButtonLabel, disableClearButton, allowClearDates, onDatesCleared, handleSubmit]);
  const skipToButtonRef = React.useRef(null);
  const dateSelectorPlayback = React.useMemo(() => {
    const {
      startLabel,
      endLabel
    } = datesInHeaderLabels || {};
    return jsx(EGDSDateSelectorPlayback, {
      endDate: endDate,
      endLabel: endLabel,
      isFullSizePlaybackDateDisplay: isFullSizePlaybackDateDisplay,
      isMultiPlaybackDateDisplay: !isSingleDateSelection,
      locale: locale,
      selectionChangeMode: selectionChangeMode,
      startDate: startDate,
      startLabel: startLabel,
      updateSelectionChangeMode: updateSelectionChangeMode,
      dateFormatOptions: dateFormatOptions
    });
  }, [dateFormatOptions, datesInHeaderLabels, endDate, isSingleDateSelection, locale, selectionChangeMode, startDate, isFullSizePlaybackDateDisplay, updateSelectionChangeMode]);
  const {
    alertTemplateComponent,
    alertTemplateData,
    startDate: selectionStart,
    endDate: selectionEnd,
    ...calendarNavigationProps
  } = props;
  const popoverCalendar = jsx(PopoverCalendar, {
    alertTemplateComponent: alertTemplateComponent,
    alertTemplateData: alertTemplateData,
    ctaItems: ctaItems,
    dateSelectorPlayback: dateSelectorPlayback,
    calendarWrapper: calendarWrapper,
    hideFooter: hideFooter,
    isOpen: isOpen,
    initialOpenState: initialOpenState,
    isSingleMonth: numColumns === 1 || numMonthsToDisplay === 1,
    popoverWidth: popoverWidth,
    showOffset: showOffset,
    showPlayback: showPlayback,
    triggerRef: triggerRef,
    children: jsx("div", {
      className: getClassNameList('uitk-date-selector'),
      ref: calendarRef,
      children: jsx(EGDSCalendarNavigation, {
        ...calendarNavigationProps,
        customAriaLabels: customAriaLabels,
        selectionStart: selectionStart,
        selectionEnd: selectionEnd,
        selectable: true
      })
    })
  });
  const composableCalendar = jsx(ComposableCalendar, {
    alertTemplateComponent: alertTemplateComponent,
    alertTemplateData: alertTemplateData,
    ctaItems: ctaItems,
    dateSelectorPlayback: dateSelectorPlayback,
    calendarWrapper: calendarWrapper,
    isOpen: isOpen,
    hideFooter: hideFooter,
    showPlayback: showPlayback,
    triggerRef: triggerRef,
    navType: calendarNavigationProps.navType,
    children: jsx("div", {
      className: getClassNameList('uitk-date-selector'),
      ref: calendarRef,
      children: jsx(EGDSCalendarNavigation, {
        ...calendarNavigationProps,
        selectionStart: selectionStart,
        selectionEnd: selectionEnd,
        selectable: true
      })
    })
  });
  const dialogCalendar = jsx(DialogCalendar, {
    alertTemplateComponent: alertTemplateComponent,
    alertTemplateData: alertTemplateData,
    ctaItems: ctaItems,
    dateSelectorPlayback: dateSelectorPlayback,
    dialogId: dialogId,
    calendarWrapper: calendarWrapper,
    showFullSizePlaybackDateDisplay: isFullSizePlaybackDateDisplay,
    hideFooter: hideFooter,
    hideDialogSubmit: hideDialogSubmit,
    isOpen: isOpen,
    sheetType: "centered",
    showPlayback: showPlayback,
    skipToButtonRef: skipToButtonRef,
    triggerRef: triggerRef,
    isNestDialog: isNestDialog,
    children: jsx("div", {
      className: getClassNameList('uitk-date-selector'),
      ref: calendarRef,
      children: jsx(EGDSCalendarNavigation, {
        ...calendarNavigationProps,
        navType: "scrolling",
        customAriaLabels: customAriaLabels,
        skipToButtonRef: skipToButtonRef,
        selectionStart: selectionStart,
        selectionEnd: selectionEnd,
        selectable: true
      })
    })
  });
  const fullscreenDialogCalendar = jsx(DialogCalendar, {
    alertTemplateComponent: alertTemplateComponent,
    alertTemplateData: alertTemplateData,
    ctaItems: ctaItems,
    dateSelectorPlayback: dateSelectorPlayback,
    dialogId: dialogId,
    calendarWrapper: calendarWrapper,
    hideDialogSubmit: hideDialogSubmit,
    isOpen: isOpen,
    sheetType: "full",
    showPlayback: showPlayback,
    skipToButtonRef: skipToButtonRef,
    triggerRef: triggerRef,
    isNestDialog: isNestDialog,
    children: jsx("div", {
      className: getClassNameList('uitk-date-selector'),
      ref: calendarRef,
      children: jsx(EGDSCalendarNavigation, {
        ...calendarNavigationProps,
        customAriaLabels: customAriaLabels,
        navType: "scrolling",
        skipToButtonRef: skipToButtonRef,
        selectionStart: selectionStart,
        selectionEnd: selectionEnd,
        selectable: true
      })
    })
  });
  switch (displayType) {
    case 'popover':
      return popoverCalendar;
    case 'dialog':
      return dialogCalendar;
    case 'fullscreenDialog':
      return fullscreenDialogCalendar;
    case 'composable':
      return composableCalendar;
    default:
      return jsxs(Viewport, {
        children: [jsx(ViewLarge, {
          children: popoverCalendar
        }), jsx(ViewMedium, {
          children: dialogCalendar
        }), jsx(ViewSmall, {
          children: fullscreenDialogCalendar
        })]
      });
  }
};
const EGDSDateSelector = props => {
  const {
    alertTemplateComponent,
    alertTemplateData,
    allowClearDates = true,
    autoCloseOnSelection = false,
    className,
    clearButtonLabel,
    customAriaLabels,
    dateFormatOptions,
    datesInHeaderLabels,
    dayData,
    dayNameLength,
    dayTemplateComponent,
    dialogId = 'default',
    enablePastDates,
    enableSameDayDateRange,
    endDate,
    endDateRef,
    calendarWrapper,
    handleShape,
    hideDialogSubmit,
    hideFooter,
    initialDisplayDate,
    initialOpenState = false,
    inputIcon1,
    inputIcon2,
    inputLabel1,
    inputLabel2,
    inputName1,
    inputName2,
    invalidInput1,
    invalidInput2,
    input1Stid,
    input2Stid,
    isSingleDateSelection = false,
    locale,
    maxDate,
    minDate,
    monthLabelAlignment,
    navType = 'inset',
    numColumns,
    numMonthsToAdvance,
    numMonthsToDisplay = 2,
    onClose,
    onDateRangeChange,
    onDateRangeClear,
    onDisabledDayClick,
    onHoverDay,
    onNextClicked,
    onOpen,
    onPrevClicked,
    onScrollHandler,
    onSelectionModeChange,
    onSubmit,
    padWeeks = false,
    popoverWidth,
    showGridlines,
    showMonthLabels,
    showOffset = false,
    showPlayback = true,
    showSiblingMonths,
    startDate,
    startDateRef,
    submitButtonLabel,
    trailingText1,
    trailingText2,
    useSingleTrigger,
    inputValue1,
    inputValue2,
    displayType,
    isFullSizePlaybackDateDisplay,
    isNestDialog,
    showDateSelector = false
  } = props;
  const DEFAULT_REFERENCE_1 = React.useRef(null);
  const DEFAULT_REFERENCE_2 = React.useRef(null);
  const input1 = startDateRef ? startDateRef : DEFAULT_REFERENCE_1;
  const input2 = endDateRef ? endDateRef : DEFAULT_REFERENCE_2;
  const calendar = React.useRef(null);
  const [monthYear, setMonthYear] = React.useState(getMonthYear(startDate, initialDisplayDate));
  const [isOpen, setIsOpen] = React.useState(initialOpenState);
  const [selectionChangeMode, setSelectionChangeMode] = React.useState('start');
  const [triggerRef, setTriggerRef] = React.useState(input1);
  useEffect(() => {
    if (showDateSelector) {
      setIsOpen(true);
    }
  }, [showDateSelector]);
  const updateSelectionChangeMode = React.useCallback(mode => {
    setSelectionChangeMode(mode);
    onSelectionModeChange && onSelectionModeChange(mode === 'end');
  }, [onSelectionModeChange]);
  const handleOpen = React.useCallback(event => {
    const isInput2Selected = event?.target === input2.current;
    const nextTriggerRef = isInput2Selected ? input2 : input1;
    const nextSelectionChangeMode = isInput2Selected && endDate ? 'end' : 'start';
    setMonthYear(getMonthYear(startDate, initialDisplayDate));
    if (isInput2Selected && getMonthYear(startDate).month !== getMonthYear(endDate).month && (numColumns === 2 || numMonthsToDisplay === 2)) {
      setMonthYear({
        month: getMonthYear(endDate).month === 0 ? 11 : getMonthYear(endDate).month -= 1,
        year: getMonthYear(endDate).month === 0 ? getMonthYear(endDate).year -= 1 : getMonthYear(endDate).year
      });
    } else if (isInput2Selected && numMonthsToDisplay === 1) {
      setMonthYear(getMonthYear(endDate, initialDisplayDate));
    }
    setIsOpen(true);
    setTriggerRef(nextTriggerRef);
    if (selectionChangeMode !== nextSelectionChangeMode) {
      updateSelectionChangeMode(nextSelectionChangeMode);
    }
    onOpen && onOpen();
  }, [endDate, initialDisplayDate, input1, input2, numColumns, numMonthsToDisplay, onOpen, selectionChangeMode, startDate, updateSelectionChangeMode]);
  const handleClearDates = React.useCallback(() => {
    updateSelectionChangeMode('start');
    onDateRangeChange && onDateRangeChange(null, null);
    onDateRangeClear && onDateRangeClear();
  }, [onDateRangeChange, onDateRangeClear, updateSelectionChangeMode]);
  const handleClose = React.useCallback(shouldClear => {
    updateSelectionChangeMode('start');
    setIsOpen(false);
    // Clear dates when selection not finished
    if (shouldClear && startDate && !(isSingleDateSelection || endDate)) {
      handleClearDates();
    }
    onClose && onClose();
  }, [endDate, handleClearDates, isSingleDateSelection, onClose, startDate, updateSelectionChangeMode]);
  const handleDateChange = (newStartDate, newEndDate) => {
    const shouldAutoClose = autoCloseOnSelection && newStartDate && (newEndDate || isSingleDateSelection);
    onDateRangeChange && onDateRangeChange(newStartDate, newEndDate);
    if (shouldAutoClose) {
      onSubmit && onSubmit(newStartDate, newEndDate);
      handleClose();
    } else if (newEndDate || isSingleDateSelection) {
      updateSelectionChangeMode('start');
    } else {
      updateSelectionChangeMode('end');
    }
  };
  const handleClickInput = React.useCallback(event => {
    if (isOpen) {
      const shouldClear = !(startDate && (isSingleDateSelection || endDate));
      handleClose(shouldClear);
    } else {
      handleOpen(event);
    }
  }, [endDate, handleClose, handleOpen, isOpen, isSingleDateSelection, startDate]);
  // Although uitk-react-calendar can receive any number for numMonthsToDisplay
  // we have a business rule that will only allow the date selector to pass 1 or 2.
  const numberOfMonthsToDisplay = React.useMemo(() => numMonthsToDisplay === 1 ? numMonthsToDisplay : 2, [numMonthsToDisplay]);
  const shouldHideDialogSubmitButton = React.useMemo(() => hideDialogSubmit && !(!autoCloseOnSelection && startDate && (endDate || isSingleDateSelection)), [autoCloseOnSelection, endDate, hideDialogSubmit, isSingleDateSelection, startDate]);
  return jsxs(EGDSLayoutPosition, {
    type: "relative",
    children: [displayType !== 'composable' && jsx(EGDSDateSelectorInputs, {
      dateFormatOptions: dateFormatOptions,
      dialogId: dialogId,
      endDate: endDate,
      handleClickInput1: handleClickInput,
      handleClickInput2: handleClickInput,
      input1Ref: input1,
      input2Ref: input2,
      inputIcon1: inputIcon1,
      inputIcon2: inputIcon2,
      inputLabel1: inputLabel1,
      inputLabel2: inputLabel2,
      inputName1: inputName1,
      inputName2: inputName2,
      invalidInput1: invalidInput1,
      invalidInput2: invalidInput2,
      input1Stid: input1Stid,
      input2Stid: input2Stid,
      isSingleDateSelection: isSingleDateSelection,
      locale: locale,
      startDate: startDate,
      inputValue1: inputValue1,
      inputValue2: inputValue2,
      trailingText1: trailingText1,
      trailingText2: trailingText2,
      useSingleTrigger: useSingleTrigger,
      isOpen: isOpen
    }), jsx(EGDSDateSelectorAdapter, {
      alertTemplateComponent: alertTemplateComponent,
      alertTemplateData: alertTemplateData,
      allowClearDates: allowClearDates,
      calendarRef: calendar,
      className: className,
      clearButtonLabel: clearButtonLabel,
      calendarWrapper: calendarWrapper,
      customAriaLabels: customAriaLabels,
      datesInHeaderLabels: datesInHeaderLabels,
      dayData: dayData,
      dayNameLength: dayNameLength,
      dayTemplateComponent: dayTemplateComponent,
      dialogId: dialogId,
      displayType: displayType,
      enablePastDates: enablePastDates,
      enableSameDayDateRange: enableSameDayDateRange,
      endDate: endDate,
      handleShape: handleShape,
      hideFooter: hideFooter,
      hideDialogSubmit: shouldHideDialogSubmitButton,
      isFullSizePlaybackDateDisplay: isFullSizePlaybackDateDisplay,
      isOpen: isOpen,
      initialOpenState: initialOpenState,
      isSingleDateSelection: isSingleDateSelection,
      locale: locale,
      maxDate: maxDate,
      minDate: minDate,
      month: monthYear.month,
      monthLabelAlignment: monthLabelAlignment,
      navType: navType,
      numColumns: numColumns,
      numMonthsToAdvance: numMonthsToAdvance,
      numMonthsToDisplay: numberOfMonthsToDisplay,
      onClose: handleClose,
      onDatesCleared: handleClearDates,
      onDisabledDayClick: onDisabledDayClick,
      onHoverDay: onHoverDay,
      onNextClicked: onNextClicked,
      onPrevClicked: onPrevClicked,
      onScrollHandler: onScrollHandler,
      onSelectionChanged: handleDateChange,
      onSubmit: onSubmit,
      padWeeks: padWeeks,
      popoverWidth: popoverWidth,
      selectionChangeMode: selectionChangeMode,
      showGridlines: showGridlines,
      showMonthLabels: showMonthLabels,
      showOffset: showOffset,
      showPlayback: showPlayback,
      showSiblingMonths: showSiblingMonths,
      startDate: startDate,
      submitButtonLabel: submitButtonLabel,
      triggerRef: triggerRef,
      updateSelectionChangeMode: updateSelectionChangeMode,
      year: monthYear.year,
      isNestDialog: isNestDialog
    })]
  });
};
EGDSDateSelector.displayName = 'EGDSDateSelector';
export { EGDSDateSelector as E };